import { Grid } from '@mui/material';
import { HSpacer, RoutalLoader, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import _ from 'lodash';
import SaveIcon from '../../../../assets/svg/SaveIcon';
import { TrashIcon } from '../../../../assets/svg/TrashIcon';
import { Modal } from '../../../../components/Modal';
import { SlidingMenuWithBack } from '../../../../components/SlidingMenuWithBack';
import { ProjectDriversFieldTypes, Stop } from '../../../../domain/stop/Stop';
import { intl } from '../../../../helpers/IntGlobalProvider';
import Button from '../../../../react-components/Button';
import { Text } from '../../../../react-components/Text';
import { useRetrieveVehicle } from '../../hooks/useRetrieveVehicle';
import { EditFields } from './edit-stop/EditFields';

interface EditStopSlidingMenuProps {
  open: boolean;
  editingStop: Stop;
  isLoading?: boolean;
  onRemoveStop: (stop: Stop) => void;
  onSaveChanges: (stop: Stop) => void;
  onExit: () => void;
  slideStyles?: React.CSSProperties;
}

export const EditStopSlidingMenu = ({
  open,
  editingStop,
  isLoading = false,
  onRemoveStop,
  onSaveChanges,
  onExit,
  slideStyles = {},
}: EditStopSlidingMenuProps) => {
  const { data: vehicleData } = useRetrieveVehicle();
  const [stop, setStop] = useState<Stop>(
    Stop.create({ ...editingStop.props, stop_fields: structuredClone(editingStop.stop_fields) }, editingStop.id)
  );
  const [values, setValues] = useState<Record<string, any>>({});
  const [isDirtyState, setIsDirtyState] = useState<boolean>(false);
  const [showDialog, setShowDialog] = useState<string | undefined>(undefined);

  const projectStopFieldsWithSections = vehicleData?.project && vehicleData.project.stop_fields_with_sections;

  const gatherValues = () => {
    if (!vehicleData?.project) return;
    let newValues: Record<string, any> = {};

    Object.values(projectStopFieldsWithSections!).forEach((section) => {
      section.forEach((field) => {
        newValues = {
          ...newValues,
          [field.id]: field.is_custom_field
            ? stop.getStopFieldCustomFieldById(field.id)?.value
            : stop.getStopFieldById(field.id)?.value,
        };
      });
    });

    setValues(newValues);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(gatherValues, []);

  if (!vehicleData?.project || !projectStopFieldsWithSections) {
    onExit();
    return null;
  }

  const updateStop = (newStop: Stop) => {
    const hasChanged = Object.values(projectStopFieldsWithSections).some((section) => {
      // Global fields
      if (!_.isEqual(newStop.label, editingStop.label)) return true;

      return section.some((field) => {
        if (field.is_custom_field) {
          const newValue = newStop.getStopFieldCustomFieldById(field.id)?.value;
          const ogValue = editingStop.getStopFieldCustomFieldById(field.id)?.value;
          return !_.isEqual(newValue ? newValue : undefined, ogValue ? ogValue : undefined);
        } else {
          const newValue = newStop.getStopFieldById(field.id)?.value;
          const ogValue = editingStop.getStopFieldById(field.id)?.value;
          return !_.isEqual(newValue ? newValue : undefined, ogValue ? ogValue : undefined);
        }
      });
    });

    setStop(newStop);
    setIsDirtyState(hasChanged);
  };

  return (
    <>
      <Modal
        open={showDialog === `dirty_state`}
        title={{
          intlId: `driver.stop.discard_edit.title`,
        }}
        subtitle={{
          intlId: `driver.stop.discard_edit.info`,
        }}
        buttons={[
          {
            intlId: `driver.stop.discard_edit.confirm`,
            onClick: onExit,
          },
          {
            intlId: `cancel`,
            onClick: () => {
              setShowDialog(undefined);
            },
            variant: `text`,
            style: {
              border: `none`,
            },
          },
        ]}
        children={<VSpacer medium />}
      />
      <Modal
        open={showDialog === `delete`}
        title={{
          intlId: `driver.stop.delete_edit.title`,
        }}
        subtitle={{
          intlId: `driver.stop.delete_edit.info`,
        }}
        buttons={[
          {
            intlId: `driver.stop.delete_edit.confirm`,
            onClick: () => {
              onRemoveStop(stop);
            },
            style: {
              backgroundColor: RoutalPalette.error.medium,
              borderColor: RoutalPalette.error.medium,
              color: RoutalPalette.white,
            },
          },
          {
            intlId: `cancel`,
            onClick: () => {
              setShowDialog(undefined);
            },
            variant: `text`,
            style: {
              border: `none`,
            },
          },
        ]}
        children={<VSpacer medium />}
      />
      <SlidingMenuWithBack
        id="edit-stop-sliding-menu"
        direction="left"
        background={RoutalPalette.white}
        isOpen={open}
        slideStyles={{
          ...slideStyles,
          padding: 0,
        }}
        headerContent={
          <Grid
            item
            container
            direction="row"
            wrap="nowrap"
            alignItems="center"
            style={{ minHeight: `70px`, padding: `10px 0` }}
          >
            <Text variant="h6" isBold>
              {stop.location?.label}
            </Text>
          </Grid>
        }
        onClose={() => {
          if (isDirtyState) setShowDialog(`dirty_state`);
          else onExit();
        }}
        childrenContainerStyles={{
          padding: 0,
          overflow: `auto`,
        }}
        footerContent={
          <Grid
            item
            container
            direction="column"
            wrap="nowrap"
            alignItems="center"
            style={{ height: `148px`, padding: `12px 20px`, borderTop: `1px solid ${RoutalPalette.neutral10}` }}
          >
            <Grid
              item
              style={{
                width: `100%`,
              }}
            >
              <Button
                style={{
                  height: `42px`,
                  borderRadius: `10px`,
                  textTransform: `none`,
                }}
                onClick={() => {
                  onSaveChanges(stop);
                }}
                disabled={!isDirtyState || isLoading}
              >
                {isLoading ? (
                  <Grid container justifyContent="center" alignItems="center">
                    <RoutalLoader width={30} />
                  </Grid>
                ) : (
                  <>
                    <Grid item container alignItems="center" justifyContent="center" style={{ width: `20px` }}>
                      <SaveIcon />
                    </Grid>

                    <HSpacer small />

                    <Text
                      style={{ color: RoutalPalette.white, fontSize: `16px` }}
                      intlId="driver.stops.edit_stop.save_changes"
                    />
                  </>
                )}
              </Button>
            </Grid>

            <Grid
              item
              style={{
                width: `100%`,
              }}
            >
              <Button
                variant="flat"
                styleType="danger"
                style={{
                  height: `40px`,
                  borderRadius: `10px`,
                  textTransform: `none`,
                }}
                onClick={() => {
                  setShowDialog(`delete`);
                }}
              >
                <Grid item container alignItems="center" justifyContent="center" style={{ width: `20px` }}>
                  <TrashIcon />
                </Grid>

                <HSpacer small />

                <Text
                  style={{ color: RoutalPalette.error.medium, fontSize: `16px` }}
                  intlId="driver.stops.edit_stop.remove_stop"
                />
              </Button>
            </Grid>
          </Grid>
        }
      >
        <Grid container direction="column" wrap="nowrap" style={{ height: `100%` }}>
          <Grid item container direction="column" wrap="nowrap" style={{ padding: `20px`, overflowY: `auto` }}>
            <Grid item>
              <Text variant="h6" intlId="driver.stops.edit_stop.information" />
              <VSpacer medium />
              {/* Global fields */}
              <EditFields
                id={`information-label`}
                fieldElement={{
                  id: `label`,
                  type: `string`,
                  value: stop.label,
                  label: intl.formatMessage({ id: `driver.stops.edit_stop.label` }),
                  is_custom_field: false,
                }}
                multiline={false}
                addData={(key, value) => {
                  stop.label = value;
                  updateStop(stop);
                }}
              />
              {projectStopFieldsWithSections.information.map((field) => {
                return (
                  <EditFields
                    id={`information-${field.id}`}
                    fieldElement={{
                      ...field,
                      type: field.type as ProjectDriversFieldTypes,
                      value: values[field.id],
                      label: intl.formatMessage({ id: `driver.stops.edit_stop.${field.id}` }),
                    }}
                    multiline={false}
                    addData={(key, value) => {
                      stop.updateStopFieldById(key, value);
                      setValues({ ...values, [key]: value });
                      updateStop(stop);
                    }}
                  />
                );
              })}
            </Grid>

            <VSpacer medium />

            <Grid item>
              {projectStopFieldsWithSections.contact.length > 0 && (
                <>
                  <Text variant="h6" intlId="driver.stops.edit_stop.contact" />
                  <VSpacer medium />
                  {projectStopFieldsWithSections.contact.map((field) => {
                    return (
                      <EditFields
                        id={`contact-${field.id}`}
                        fieldElement={{
                          ...field,
                          type: field.type as ProjectDriversFieldTypes,
                          value: values[field.id],
                          label: intl.formatMessage({ id: `driver.stops.edit_stop.${field.id}` }),
                        }}
                        addData={(key, value) => {
                          stop.updateStopFieldById(key, value);
                          setValues({ ...values, [key]: value });
                          updateStop(stop);
                        }}
                      />
                    );
                  })}
                </>
              )}
            </Grid>

            <VSpacer medium />

            <Grid item>
              {projectStopFieldsWithSections.constraints.length > 0 && (
                <>
                  <Text variant="h6" intlId="driver.stops.edit_stop.constraints" />
                  <VSpacer medium />
                  {projectStopFieldsWithSections.constraints.map((field) => {
                    return (
                      <EditFields
                        id={`constraints-${field.id}`}
                        fieldElement={{
                          ...field,
                          type: field.type as ProjectDriversFieldTypes,
                          value: values[field.id],
                          label: intl.formatMessage({ id: `driver.stops.edit_stop.${field.id}` }),
                        }}
                        addData={(key, value) => {
                          stop.updateStopFieldById(key, value);
                          setValues({ ...values, [key]: value });
                          updateStop(stop);
                        }}
                      />
                    );
                  })}
                </>
              )}
            </Grid>

            <VSpacer medium />

            <Grid item>
              {projectStopFieldsWithSections.custom_fields.length > 0 && (
                <>
                  <Text variant="h6" intlId="driver.stops.edit_stop.custom_fields" />
                  <VSpacer medium />
                  {projectStopFieldsWithSections.custom_fields.map((field) => {
                    return (
                      <EditFields
                        id={`custom_fields-${field.id}`}
                        fieldElement={{
                          ...field,
                          type: field.type as ProjectDriversFieldTypes,
                          value: values[field.id],
                        }}
                        addData={(key, value) => {
                          stop.updateStopFieldCustomFieldById(key, value);
                          setValues({ ...values, [key]: value });
                          updateStop(stop);
                        }}
                      />
                    );
                  })}
                </>
              )}
            </Grid>
          </Grid>
        </Grid>
      </SlidingMenuWithBack>
    </>
  );
};
